import '../App.sass';
import { ActionButton, CommandBarButton, DefaultButton, IButtonStyles, IconButton, PrimaryButton } from '@fluentui/react/lib/Button';
import Section from '../components/Section';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog, { DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { TextField } from '@fluentui/react/lib/TextField';
import { Pivot, PivotItem, Rating, RatingSize, ThemeProvider, Toggle, Checkbox, ChoiceGroup, OverflowSet, MessageBarType, MessageBar, TooltipHost, IOverflowSetItemProps } from '@fluentui/react';
import { MCPClient, OrderItem, Plan, ServiceType } from '@the-real-insight/tri-model';
import { PaymentMode } from '@the-real-insight/tri-model';
import { InvoicingPeriod } from '@the-real-insight/tri-model';
import { useCustomerTheme } from '../Theme';
import { useModalContext } from '../components/ModalProvider';
import DataTable from 'react-data-table-component';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';
import { userAPI } from '../UserAPI';
import { useSearchParams } from 'react-router-dom';
import RAGManagement from '../real-gain/rag/RAGManagement';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import AgentToolsetManagement from '../real-gain/AgentToolsetManagement';
import { useTestDialog } from '../real-gain/hooks/useTestDialog';
import { usePublishDialog } from '../real-gain/hooks/usePublishDialog';
import dayjs from 'dayjs';

interface Properties {
}

const dummyToolSets = [{
    predefined: true,
    status: 'purchased',
    provider: {
        name: 'The Real Insight GmbH',
        address: {
            street: 'Beilsteiner Weg',
            streetNumber: '22',
            postalCode: '56479',
            city: 'Seck',
        },
        email: 'info@the-real-insight.com',
        website: 'www.the-real-insight.com',
    },
    ranking: 4.8,
    name: 'The Real Insight',
    synopsis: 'Daten aus mehr als 350 öffentlichen Datenbeständen und mannigfaltige Tools zur Gewinnung von Informationen über Gebäude, Bauwerke und Grundstücke.'
}, {
    status: 'offered',
    provider: {
        name: 'Musterfirma',
        address: {
            street: 'Musterstraße',
            streetNumber: '1',
            postalCode: '12345',
            city: 'Musterstadt',
        },
        email: 'info@musterfirma.de',
        website: 'http://localhost:3066/doc/index.html'
    },
    name: 'Referenzimplementierung',
    ranking: 4.1,
    synopsis: 'CO2-Optimierungsmaßnahmen und Informationen über Immobilien- und Facility Management-Organisationen in Deutschland.',
}/*, {
    status: 'offered',
    provider: 'Fraunhofer-Institut für Bauphysik IBP',
    address: 'Fraunhoferstr. 10, 83626 Valley',
    email: 'info@fraunhofer.de',
    website: 'https://www.bau.fraunhofer.de',
    ranking: 4.1,
    description: 'Tools zur bauphysikalischen Analyse von Gebäudedaten.',
}, {
    status: 'ordered',
    provider: 'Smart Building Innovation gGmbH',
    address: 'Lietzenburger Str. 44/46, 10789 Berlin',
    email: 'info@sbif.foundation',
    website: 'www.sbif.foundation',
    ranking: 3.5,
    description: 'Tools zur Bewertung von Massnahmen zur Optimierung des nachhaltigen Betriebs von Gebäuden.',
}*/];

export default function AgentPanel(properties: Properties) {
    const { t } = useTranslation();
    const { theme, setTheme } = useCustomerTheme();
    const [authentication] = useAtom(authenticationAtom) as any;
    const [searchParams, setSearchParams] = useSearchParams();
    const { pushError, showMessageDialog } = useModalContext() as any;
    const [toolSets, setToolSets] = useState(dummyToolSets);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [orderDialogOpen, setOrderDialogOpen] = useState(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [toolFilter, setToolFilter] = useState('all');
    const [searchFilter, setSearchFilter] = useState('');
    const [filteredToolSets, setFilteredToolSets] = useState(toolSets);
    const [tab, setTab] = useState('marketplace');
    const [marketplaceOffers, setMarketplaceOffers] = useState([]) as any;
    const [topMarketplaceOffers, setTopMarketplaceOffers] = useState([]) as any;
    const [selectedMarketplaceOffer, setSelectedMarketplaceOffer] = useState<any>(null);
    const onRenderItemStyles = {
        root: { padding: '10px' },
    };

    const { TestDialog, openTestDialog } = useTestDialog({
        onOrder: (marketplaceOffer) => {
            setSelectedMarketplaceOffer(marketplaceOffer);
            setOrderDialogOpen(true);
        }
    });

    const { PublishDialog, openPublishDialog } = usePublishDialog({
        onPublish: async () => {
            // Add your publish logic here
        }
    });

    const toolsetColumns = [{
        name: '',
        width: '80px',
        cell: (marketPlaceOffer: any) => <div className='flexGrow1 displayFlex justifyContentCenter'>
            <div className='flexGrow1 displayFlex justifyContentEnd'>
                <ActionButton iconProps={{ iconName: 'Rocket' }} onClick={() => openTestDialog(marketPlaceOffer)}></ActionButton>
                <ActionButton iconProps={{ iconName: 'ShoppingCart' }} onClick={() => openOrderDialog(marketPlaceOffer)}></ActionButton>
            </div>
        </div>
    }, {
        name: '',
        width: '120px',
        cell: (marketPlaceOffer: any) => <div className="paddingS">
            {marketPlaceOffer.images && marketPlaceOffer.images.length > 0
                ?
                <Zoom>
                    <img src={userAPI.getResourceURL(marketPlaceOffer.images[0])} alt={marketPlaceOffer.name} className='width90 height60' />
                </Zoom>
                :
                <div className='width90 height60 backgroundColorPrimaryLightest borderNeutral'></div>
            }
        </div>
    }, {
        name: 'Agenten-Werkzeugsatz/Wissensbasis',
        width: '500px',
        cell: (marketPlaceOffer: any) => <div>
            <div className='textM fontWeightBold'>{marketPlaceOffer.name}</div>
            <div className='textS'>{marketPlaceOffer.synopsis}</div>
        </div>
    }, {
        name: 'Anbieter',
        width: '300px',
        cell: (marketPlaceOffer: any) => <div>
            <div>{marketPlaceOffer.provider?.name}</div>
            <div>{marketPlaceOffer.provider?.address?.street} {marketPlaceOffer.provider?.address?.streetNumber}, {marketPlaceOffer.provider?.address?.city}</div>
            <div><a href={`mailto:${marketPlaceOffer.provider?.email}`}>{marketPlaceOffer.provider?.email}</a></div>
            <div><a href={marketPlaceOffer.provider?.website} target='_blank'>{marketPlaceOffer.provider?.website}</a></div>
        </div>
    }, {
        name: 'Bewertung',
        width: '200px',
        cell: (toolSet: any) => <div>{toolSet.predefined ? <></> : <Rating
            max={5}
            size={RatingSize.Small}
            defaultRating={toolSet.ranking}
            ariaLabel="Large stars"
            ariaLabelFormat="{0} of {1} stars"
        />}</div>
    }];

    const openOrderDialog = async (marketPlaceOffer: any) => {
        if (marketPlaceOffer.plans.length === 0) {
            showMessageDialog('Keine Preismodelle verfügbar', 'Für dieses Marktplatzangebot sind keine Preismodelle definiert.');
            return;
        }

        if (marketPlaceOffer._tenant === authentication.customer) {
            showMessageDialog('Nicht autorisierter Zugriff', 'Du kannst kein Marktplatzangebot von deinem eigenen Unternehmen    bestellen.');
            return;
        }

        setSelectedMarketplaceOffer(marketPlaceOffer);
        setSelectedPlan(marketPlaceOffer.plans[0].name);
        setOrderDialogOpen(true);
    }

    const openDetailsDialog = async (marketPlaceOffer: any) => {
        setSelectedMarketplaceOffer(marketPlaceOffer);
        setDetailsDialogOpen(true);
    }

    const deleteServer = async (server: any) => {
        const mcpClient = new MCPClient();

        try {
            //await mcpClient.deleteServer(server.id);
        } catch (error) {
            console.error(error);
            pushError(error);
        }
    }

    const setParams = (value: any) => {
        searchParams.set('tab', value.key.substring(1));
        setSearchParams(searchParams);
        setTab(value.key.substring(1));
    }

    const getRandomIndexes = (count: number) => {
        const indexes = new Set<number>();
        while (indexes.size < count && indexes.size < marketplaceOffers.length) {
            indexes.add(Math.floor(Math.random() * marketplaceOffers.length));
        }
        return Array.from(indexes);
    }

    const getRandomMarketplaceOffers = (marketplaceOffers: any[]) => {
        const indexes = getRandomIndexes(3);
        return marketplaceOffers.filter((_, index) => indexes.includes(index));
    }

    const loadMarketplaceOffers = async () => {
        const newMarketplaceOffers = await userAPI.getMarketplaceOffers(authentication.customer);

        console.log('newMarketplaceOffers >>>', newMarketplaceOffers);

        setMarketplaceOffers(newMarketplaceOffers);
        setTopMarketplaceOffers(getRandomMarketplaceOffers(newMarketplaceOffers));
    }

    useEffect(() => {
        loadMarketplaceOffers();
    }, []);

    useEffect(() => {
        if (!searchFilter) {
            setFilteredToolSets([...marketplaceOffers, ...toolSets]);
            return;
        }

        const filter = searchFilter.toLowerCase();
        const filtered = [...marketplaceOffers, ...toolSets].filter((toolSet: any) =>
            toolSet.provider?.name.toLowerCase().startsWith(filter) ||
            toolSet.name?.toLowerCase().startsWith(filter) ||
            toolSet.description?.toLowerCase().startsWith(filter)
        );
        setFilteredToolSets(filtered);
    }, [searchFilter, toolSets, marketplaceOffers]);

    return <><div>
        <div className='displayFlex textHeader'>REAL GAIN Agenten</div>
        <Pivot className='marginTopM' aria-label="" onLinkClick={setParams} selectedKey={tab}>
            <PivotItem id="marketplace" headerText={'Marktplatz'} itemIcon="UserOptional">
                <div className="marginTopM textHeading">Beliebte REAL GAIN Agenten</div>
                <div className='marginTopM width300 displayFlex gapM'>
                    {topMarketplaceOffers.map((offer: any, index: number) => <div key={index}>
                        <div className='displayFlex flexDirectionColumn gapS'>
                            <div className='displayFlex flexDirectionColumn gapS hoverHand' onClick={() => {
                                setSelectedMarketplaceOffer(offer);
                                openDetailsDialog(offer);
                            }}>
                                {offer.images && offer.images.length > 0
                                    ?
                                    <img src={userAPI.getResourceURL(offer.images[0])} alt={offer.name} className='width300 height200' />
                                    :
                                    <div className='width300 height200 backgroundColorPrimaryLightest borderNeutral'></div>
                                }
                                <div className='textSubHeading'>{offer.name}</div>
                                <div className='height80 textS'>{offer.synopsis}</div>
                                {offer.provider && offer.provider.address
                                    ?
                                    <div className='textS colorPrimary'>{offer.provider.name}, {offer.provider.address.city}</div>
                                    :
                                    <></>}
                            </div>
                            <div className='displayFlex'><Rating
                                max={5}
                                size={RatingSize.Small}
                                defaultRating={Math.round(Math.random() * 5)}
                                ariaLabel="Rating"
                                ariaLabelFormat="{0} of {1} stars"
                            />
                                <div className='flexGrow1 displayFlex justifyContentEnd'>
                                    <ActionButton iconProps={{ iconName: 'Rocket' }} onClick={() => openTestDialog(offer)}></ActionButton>
                                    <ActionButton iconProps={{ iconName: 'ShoppingCart' }} onClick={() => openOrderDialog(offer)}></ActionButton>
                                </div>
                            </div>
                        </div>
                    </div>)}
                </div>
                <div className="marginTopM textHeading">Alle Agenten</div>
                <div className='marginTopM'>
                    <div className='textM'>REAL GAIN Agenten, die du zur Bearbeitung von Fragen durch deine Nutzer verwenden kannst.</div>
                    <div className='marginTopM displayFlex alignItemsEnd gapL'>
                        <TextField
                            placeholder={'Filter eingeben'}
                            underlined
                            value={searchFilter}
                            onChange={(_, newValue) => setSearchFilter(newValue || '')}
                            styles={{
                                root: {
                                    width: '200px'
                                },
                                field: {
                                    border: 'none !important',
                                    borderBottom: '1px solid #8a8886',
                                    borderRadius: '0',
                                    padding: '0 0 4px 0'
                                },
                                fieldGroup: {
                                    border: 'none !important',
                                    borderRadius: '0'
                                }
                            }}
                        />
                        <ChoiceGroup
                            selectedKey={toolFilter}
                            options={[
                                { key: 'all', text: t('settingsPanel.agentPanel.filterTools.all') },
                                { key: 'used', text: t('settingsPanel.agentPanel.filterTools.used') },
                                { key: 'unused', text: t('settingsPanel.agentPanel.filterTools.unused') }
                            ]}
                            onChange={(_, option) => option && setToolFilter(option.key)}
                            styles={{
                                flexContainer: {
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '20px'
                                }
                            }}
                        />
                    </div>
                    <div className='marginTopM borderNeutral'>
                        <DataTable data={filteredToolSets}
                            columns={toolsetColumns}
                            pagination
                        />
                    </div>
                </div>
            </PivotItem>
            <PivotItem id="agentToolset" headerText={'Eigene Agenten-Werkzeugsätze'} itemIcon="ShoppingCart">
                <div className='marginTopM'>
                    <AgentToolsetManagement />
                </div>
            </PivotItem>
            <PivotItem id="documentPacks" headerText={'Eigene Wissensbasen'} itemIcon="Document">
                <div className='marginTopM'>
                    <RAGManagement />
                </div>
            </PivotItem>
        </Pivot>
    </div >
        <Dialog
            hidden={!orderDialogOpen}
            onDismiss={() => setOrderDialogOpen(false)}
            maxWidth={600}
            minWidth={600}
            dialogContentProps={{
                type: DialogType.normal,
                title: t('settingsPanel.agentPanel.orderToolset'),
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            {selectedMarketplaceOffer
                ?
                <div>
                    <div className='textM'>Wähle eines der folgenden Preismodelle</div>
                    <div className="marginTopM borderNeutral height400px overflowYScroll">
                        {(selectedMarketplaceOffer.plans || []).map((plan: any) => <div>
                            <div className='paddingM marginBottomS' style={{ backgroundColor: selectedPlan === plan.name ? '#EFEFEF' : '#FFFFFF' }} onClick={() => {
                                setSelectedPlan(plan.name);
                            }}>
                                <div className='textHeading'>{plan.name}</div>
                                <div className='marginTopS textM'>{plan.description}</div>
                            </div>
                        </div>)}
                    </div>
                    <div className='marginTopM displayFlex alignItemCenter gapS'>
                        <Checkbox
                            checked={agreedToTerms}
                            onChange={(ev, checked) => setAgreedToTerms(checked || false)}
                        />
                        <div className='textM'>Ich bestelle hiermit verbindlich zu obigem Abrechnungsmodell, stimme den folgenden <a href={selectedMarketplaceOffer.termsAndConditionsURL} target='_blank'>Geschäftsbedingungen</a> zu und akzeptiere die <a href={selectedMarketplaceOffer.dataProtectionURL} target='_blank'>Datenschutzrichtlinien</a> der <a href={selectedMarketplaceOffer.provider.website} target='_blank'>{selectedMarketplaceOffer.provider.name}</a>.</div>
                    </div>
                </div>
                :
                <></>
            }
            <DialogFooter className="marginTopXXL">
                <PrimaryButton
                    text={t('settingsPanel.agentPanel.order')}
                    disabled={!selectedPlan || !agreedToTerms}
                    onClick={async () => {
                        try {
                            console.log('======> selectedPlan', selectedPlan);
                            console.log('======> offer', selectedMarketplaceOffer);

                            const order = {
                                seller: selectedMarketplaceOffer._tenant,
                                customer: authentication.customer,
                                orderDate: new Date(),
                                expirationDate: dayjs().add(1, 'year').toDate(),
                                paymentMode: PaymentMode.upFront,
                                billingPeriod: InvoicingPeriod.monthly,
                                orderItems: [new OrderItem({
                                    service: selectedMarketplaceOffer._type === 'KnowledgeBase' ? ServiceType.knowledgeBaseUsage : ServiceType.agentToolsetUsage,
                                    serviceReference: selectedMarketplaceOffer._id,
                                    plan: selectedMarketplaceOffer.plans.find((plan: any) => plan.name === selectedPlan)
                                })]
                            };

                            console.log('======> order', order);

                            userAPI.createServiceOrder(authentication.customer, order);

                            setOrderDialogOpen(false);
                        } catch (error) {
                            pushError(error);
                        }
                    }}
                />
                <DefaultButton onClick={() => {
                    setOrderDialogOpen(false);
                }} text={t('global.cancel')} />
            </DialogFooter>
        </Dialog>
        <Dialog
            hidden={!detailsDialogOpen}
            onDismiss={() => setDetailsDialogOpen(false)}
            maxWidth={1000}
            minWidth={800}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Toolset Details',
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            {selectedMarketplaceOffer
                ?
                < div className='displayFlex flexDirectionColumn gapS'>
                    <div className='displayFlex alignItemsCenter gapM'>
                        <ActionButton iconProps={{ iconName: 'Document' }} onClick={() => window.open(selectedMarketplaceOffer.termsAndConditionsURL, '_blank')}>Geschäftsbedingungen</ActionButton>
                        <ActionButton iconProps={{ iconName: 'Shield' }} onClick={() => window.open(selectedMarketplaceOffer.dataProtectionURL, '_blank')}>Datenschutz</ActionButton>
                    </div>
                    <div className='marginTopS textM fontWeightBold'>{selectedMarketplaceOffer.synopsis}</div>
                    <div className='marginTopS'>
                        <Markdown rehypePlugins={[rehypeRaw] as any} remarkPlugins={[remarkGfm]}>{selectedMarketplaceOffer.marketingContent}</Markdown>
                    </div>
                </div>
                :
                <></>
            }
            <DialogFooter className="marginTopXXL">
                <PrimaryButton onClick={() => {
                    openOrderDialog(selectedMarketplaceOffer);
                    setDetailsDialogOpen(false);
                }} text={t('settingsPanel.agentPanel.order')} />
                <PrimaryButton onClick={() => {
                    openTestDialog(selectedMarketplaceOffer);
                    setDetailsDialogOpen(false);
                }} text={t('settingsPanel.agentPanel.test')} />
                <DefaultButton onClick={() => {
                    setDetailsDialogOpen(false);
                }} text={t('global.close')} />
            </DialogFooter>
        </Dialog >
        <TestDialog />
        <PublishDialog />
    </>;
}